<template>
    <div class="draft-details-form">
        <div class="field">
            <label for="draft-details-title">Title</label>
            <InputText
                id="draft-details-title"
                v-model="title"
                autofocus
                maxlength="200"
                class="w-full"
                @keydown.enter="save"
            />
        </div>
        <div class="field">
            <label for="draft-details-description"
                >Description (optional)</label
            >
            <Textarea
                id="draft-details-description"
                v-model="description"
                rows="4"
                autoResize
                class="w-full"
                placeholder="What is this draft changing, and why?"
            />
        </div>
        <div class="draft-details-actions">
            <Button label="Cancel" text severity="secondary" @click="close" />
            <Button
                label="Save"
                :loading="saving"
                :disabled="!title.trim() || saving"
                data-testid="draft-details-save"
                @click="save"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import { type Ref, inject, ref } from "vue";

import { type ThreadSchema, useThreadsStore } from "@/stores/threads-store";

type DraftDetailsDialog = DynamicDialogInstance & {
    data: { thread: ThreadSchema };
};

const dialogRef = inject<Ref<DraftDetailsDialog>>("dialogRef");
const thread = dialogRef?.value.data.thread;
const title = ref(thread?.title ?? "");
const description = ref(thread?.description ?? "");
const originalDescription = thread?.description ?? "";
const saving = ref(false);
const store = useThreadsStore();

const close = () => dialogRef?.value.close();

const save = async () => {
    const nextTitle = title.value.trim();
    if (!thread || !nextTitle || saving.value) return;

    saving.value = true;
    try {
        const nextDescription = description.value.trim();
        const descriptionUnchanged = nextDescription === originalDescription;
        const updated = await store.updateThread(
            thread.identity,
            nextTitle,
            descriptionUnchanged ? null : nextDescription
        );
        if (updated) dialogRef?.value.close(updated);
    } finally {
        saving.value = false;
    }
};
</script>

<style scoped>
.draft-details-form,
.field {
    display: flex;
    flex-direction: column;
}

.draft-details-form {
    gap: 1rem;
}

.field {
    gap: 0.35rem;
}

.field label {
    font-size: 0.9rem;
    font-weight: 500;
}

.draft-details-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}
</style>
